import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment'
import Group from 'components/utils/Group'
import NumberRating from 'components/Common/NumberRating'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import Divider from 'LuxKit/Divider'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import styled from 'styled-components'
import { rem } from 'polished'
import {
  OfferDetailImageGallery,
  OfferDetailDescription,
  OfferDetailReviews,
  OfferDetailMap,
} from '../OfferDetail'
import type { OfferDetailReview, OfferDetailGeoLocation } from '../OfferDetail'
import type { ExperienceDetailResult, TripItineraryData } from 'tripPlanner/api/types'
import ExperienceDetailMetadata from './ExperienceDetailMetadata'
import ExperienceDetailPricing, { type TripExperienceSelection } from './ExperienceDetailPricing'
import getDrawerBookableUntilDate from './getDrawerBookableUntilDate'
import { isExperienceSavedData } from 'tripPlanner/utils/savedItemDataGuards'
import { findOfferItemInItinerary } from 'tripPlanner/utils/chatItinerary'
import { appendContextTag, TRIP_PLANNER_ACTION_CONTEXT_LABEL } from 'tripPlanner/utils/contextTag'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'

const ContentWrapper = styled.div`
  margin: 0 auto;
  padding: ${rem(24)};
  width: ${rem(700)};
  maxwidth: 100%;
  box-sizing: border-box;
`

const CategoryLabel = styled(BodyText)`
  text-transform: capitalize;
`

export type DrawerExperience =
  Omit<ExperienceDetailResult, 'images' | 'redemptionLocations' | 'geoLocation'> & {
    images: Array<App.Image>
    geoLocation?: OfferDetailGeoLocation
  }

export type DrawerExperienceSearchTile = DrawerExperience & Partial<Pick<App.ExperienceOffer, 'copy' | 'giftType' | 'productType'>>

interface Props {
  experience: DrawerExperience
  standaloneOffer?: App.ExperienceOffer
  reviews?: Array<OfferDetailReview>
  reviewsLoading?: boolean
  itineraryData?: TripItineraryData
  onSendMessage?: (message: string) => void
  scrollToPricing?: boolean
}

function ExperienceDetailView({
  experience,
  standaloneOffer,
  reviews,
  reviewsLoading,
  itineraryData,
  onSendMessage,
  scrollToPricing,
}: Props) {
  const intl = useIntl()
  const {
    name,
    location,
    description,
    images,
    rating,
    reviewsTotal,
    geoLocation,
    deeplinkUrl,
    price,
    memberPrice,
    memberDiscountPercent,
    value,
    currencyCode,
    duration,
    categories,
    isSoldOut,
  } = experience

  const hasDescription = !!description
  const hasReviews = !!reviews && reviews.length > 0
  const hasGeoLocation = !!geoLocation
  const categoryLabels = categories.map((c) => c.label).join(', ')
  const bookableUntilDate = getDrawerBookableUntilDate(standaloneOffer)

  const tripItem = itineraryData ? findOfferItemInItinerary(experience.offerId, itineraryData) : undefined
  const experienceSavedData = tripItem?.savedItemData && isExperienceSavedData(tripItem.savedItemData) ? tripItem.savedItemData : undefined

  const initialPageState = experienceSavedData ? {
    date: experienceSavedData.date ?? undefined,
    time: experienceSavedData.time ?? undefined,
    ticketCounts: Object.fromEntries(
      (experienceSavedData.tickets ?? []).map(t => [t.ticketId, t.count]),
    ),
    redemptionLocationId: experienceSavedData.redemptionLocationId ?? undefined,
    pickupPointId: experienceSavedData.pickupPointId ?? undefined,
  } : undefined

  const handleSaveToTrip = useCallback((selection: TripExperienceSelection) => {
    if (!onSendMessage || !tripItem) return

    const totalTickets = selection.tickets.reduce((sum, t) => sum + t.count, 0)
    const ticketSummary = intl.formatMessage(
      {
        id: 'l9VadRxewhw3rkCSE0hh',
        defaultMessage: '{count, plural, one {# ticket} other {# tickets}}',
        description: 'Ticket count summary used in the save-to-trip confirmation message',
      },
      { count: totalTickets },
    )

    const dateTimeSuffix = [
      selection.date ? ` on ${moment(selection.date).format(DMY_CASUAL_FORMAT)}` : '',
      selection.time ? ` at ${selection.time}` : '',
    ].join('')

    const visible = intl.formatMessage(
      {
        id: 'jUrBvdHhRPs1mrOFU1ta',
        defaultMessage: "I've selected {tickets} for {name}{dateTime}",
        description: 'Message sent to the trip planner AI when user saves experience ticket selection',
      },
      { tickets: ticketSummary || 'tickets', name, dateTime: dateTimeSuffix },
    )

    const hidden = [
      `item_id=${tripItem.id}`,
      `experience_id=${experience.offerId}`,
      `date=${selection.date ?? 'none'}`,
      `time=${selection.time ?? 'none'}`,
      `tickets=${selection.tickets.map(t => `${t.count}x${t.ticketId}`).join(',')}`,
      selection.redemptionLocationId ? `redemption_location_id=${selection.redemptionLocationId}` : '',
      selection.pickupPointId ? `pickup_point_id=${selection.pickupPointId}` : '',
    ].filter(Boolean).join(', ')

    onSendMessage(appendContextTag(visible, hidden, TRIP_PLANNER_ACTION_CONTEXT_LABEL))
  }, [onSendMessage, tripItem, name, experience.offerId, intl])

  return (
    <ContentWrapper>
      <Group direction="vertical" gap={24}>
        <Group direction="vertical" gap={12}>
          <Group direction="vertical">
            <Heading variant="heading2">{name}</Heading>
            <BodyText variant="medium" colour="light-foreground-alt">{location}</BodyText>
            {categoryLabels && (
              <CategoryLabel variant="small" colour="light-foreground-alt">{categoryLabels}</CategoryLabel>
            )}
          </Group>
          {rating !== undefined && rating > 0 && (
            <NumberRating
              rating={rating}
              total={reviewsTotal}
              variant="small"
            />
          )}
        </Group>

        <OfferDetailImageGallery images={images} />

        <ExperienceDetailMetadata
          duration={duration}
          bookableUntilDate={bookableUntilDate}
        />

        {hasDescription && (
          <>
            <Divider kind="secondary" />
            <OfferDetailDescription description={description} />
          </>
        )}

        <Divider kind="secondary" />

        <ExperienceDetailPricing
          key={standaloneOffer ? 'loaded' : 'loading'}
          price={price}
          memberPrice={memberPrice ?? undefined}
          memberDiscountPercent={memberDiscountPercent ?? undefined}
          value={value}
          currencyCode={currencyCode}
          deeplinkUrl={deeplinkUrl}
          isSoldOut={isSoldOut}
          standaloneOffer={standaloneOffer}
          initialPageState={initialPageState}
          onSaveToTrip={tripItem ? handleSaveToTrip : undefined}
          scrollToPricing={scrollToPricing}
        />

        {(hasReviews || reviewsLoading) && (
          <>
            <Divider kind="secondary" />
            {reviewsLoading && !hasReviews && (
              <Group direction="vertical" gap={12}>
                <LoadingBox height={20} width={120} borderRadius="S" />
                <LoadingBox height={60} width={320} borderRadius="S" />
              </Group>
            )}
            {hasReviews && (
              <OfferDetailReviews
                rating={rating}
                reviewsTotal={reviewsTotal}
                reviews={reviews}
              />
            )}
          </>
        )}

        {hasGeoLocation && (
          <>
            <Divider kind="secondary" />
            <OfferDetailMap
              geoLocation={geoLocation}
              offerName={name}
            />
          </>
        )}
      </Group>
    </ContentWrapper>
  )
}

export default ExperienceDetailView
